/**
 * ч.752 (рішення власника): попап «рахунки контрагента» біля IBAN у платіжці —
 * список банк-рахунків з картки контрагента (клік → підставити) + додати новий
 * (IBAN + валюта + назва) з одночасним збереженням у картку контрагента.
 * Назва банку — з довідника за МФО IBAN.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contractorNested } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { findBankByIban, type BankRow } from '@/shared/ukr_classifiers';
import TchTable from '@/shared/components/TchTable';

export default function ContractorBankPicker(props: {
  contractorId: string;
  onPick: (iban: string) => void;
  onClose: () => void;
}) {
  const { contractorId, onPick, onClose } = props;
  const qc = useQueryClient();
  const [iban, setIban] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const accQ = useQuery({
    queryKey: ['contractor-bank-accounts', contractorId],
    queryFn: () => refs.contractorBankAccounts(contractorId),
  });
  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: refs.currencies });
  const banksQ = useQuery({
    queryKey: ['banks'], queryFn: () => refs.banks() as Promise<BankRow[]>, staleTime: 3_600_000,
  });
  const uahId = (currenciesQ.data ?? []).find((c) => c.code === 'UAH')?.id ?? '';
  const clean = iban.replace(/\s+/g, '').toUpperCase();
  const liveBank = findBankByIban(clean, banksQ.data ?? []);

  const create = useMutation({
    mutationFn: () => contractorNested.createBank({
      contractor_id: contractorId, iban: clean, currency_id: currencyId || uahId,
      account_name: name.trim() || null, is_main: (accQ.data?.length ?? 0) === 0,
    }),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['contractor-bank-accounts', contractorId] });
      onPick(row.iban);
      onClose();
    },
    onError: (e) => setErr((e as { response?: { data?: { detail?: string } } })
      ?.response?.data?.detail ?? 'Не вдалося зберегти рахунок'),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 60,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div className="card" style={{ width: 640, maxWidth: '95vw', padding: '1rem' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Банк-рахунки контрагента</h3>
          <button type="button" style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>
        {(accQ.data?.length ?? 0) === 0 && (
          <p style={{ color: 'var(--muted)', margin: '0 0 0.5rem' }}>
            {accQ.isLoading ? 'Завантаження…' : 'У картці контрагента рахунків немає — додайте нижче.'}
          </p>
        )}
        {(accQ.data?.length ?? 0) > 0 && (
          <TchTable style={{ marginBottom: '0.75rem' }}>
            <thead>
              <tr><th>IBAN</th><th>Банк</th><th>Назва</th><th style={{ width: 80 }}></th></tr>
            </thead>
            <tbody>
              {accQ.data!.map((a) => (
                <tr key={a.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{a.iban}{a.is_main ? ' ✓' : ''}</td>
                  <td>{findBankByIban(a.iban, banksQ.data ?? []) ?? '—'}</td>
                  <td>{a.account_name ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button type="button" className="primary"
                      onClick={() => { onPick(a.iban); onClose(); }}>обрати</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TchTable>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr auto', gap: '0.5rem', alignItems: 'end' }}>
          <div className="field" style={{ margin: 0 }}>
            <label>Новий IBAN
              {liveBank && <small style={{ color: 'green', marginLeft: 6 }}>{liveBank}</small>}
            </label>
            <input value={iban} maxLength={34} placeholder="UA…"
              onChange={(e) => setIban(e.target.value)} />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Валюта</label>
            <select value={currencyId || uahId} onChange={(e) => setCurrencyId(e.target.value)}>
              {(currenciesQ.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
            </select>
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Назва рахунку</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <button type="button" className="primary" disabled={clean.length < 15 || create.isPending}
            title="Зберегти у картку контрагента і підставити у платіжку"
            onClick={() => { setErr(null); create.mutate(); }}>
            додати і обрати
          </button>
        </div>
        {err && <div className="error" style={{ marginTop: '0.5rem' }}>{err}</div>}
      </div>
    </div>
  );
}
